import { TicketIcon, XIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { Timeline } from '@/components/common/timeline';
import { Drawer } from '@/components/feedback/drawer';
import { ImageLightbox } from '@/components/feedback/image-lightbox';
import { routes } from '@/config/routes';
import {
  REPLACEMENT_REASONS,
  REPLACEMENT_STATUS_CHIP,
  timelineChipIntent,
} from '@/features/requests';

import { DrawerSkeleton } from '../../components/drawer-skeleton';
import { unitLabel } from '../format';
import { useOrderReplacementRequest } from '../hooks';
import { deriveReplacementTimeline, formatTimelineStamp } from '../timeline';
import type { OrderReplacementDetail } from '../types';

import styles from './replacement-preview-drawer.module.scss';

/** AC-shaped failure copy, mirroring the listing drawer's ("…your replacement requests…"). */
const LOAD_ERROR = "We couldn't load this replacement request. Please try again.";

export type ReplacementPreviewDrawerProps = {
  orderId: string;
  requestId: string;
  open: boolean;
  onClose: () => void;
};

function HeaderTitle({ status }: { status?: OrderReplacementDetail['status'] }) {
  const chip = status ? REPLACEMENT_STATUS_CHIP[status] : undefined;
  return (
    <span className={styles.header}>
      <span className={styles.iconBadge}>
        <TicketIcon size={20} weight="fill" aria-hidden />
      </span>
      <span className={styles.headerHeading}>Ticket Preview</span>
      {chip ? <Chip intent={chip.intent}>{chip.label}</Chip> : null}
    </span>
  );
}

/**
 * Read-only Ticket Preview for one order-scoped replacement request (112368). A deliberate sibling
 * of the queue's `ReplacementDetailDrawer`, not a generalisation of it: the payloads barely overlap
 * and the rejection block sits below the timeline here and above it there (ADR-0022, grilling Q3).
 *
 * Nothing here can be acted on — no approve, reject, cancel or revert. Approving or rejecting
 * happens in the Requests → Replacements queue. Closing pops back to the listing underneath.
 */
export function ReplacementPreviewDrawer({
  orderId,
  requestId,
  open,
  onClose,
}: ReplacementPreviewDrawerProps) {
  const { data: detail, isPending, isError } = useOrderReplacementRequest(orderId, requestId, open);
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  /** Bound up front rather than narrowed in JSX: TypeScript drops property narrowing inside the
   *  button's click closure, and a non-null assertion there would be a lint error. */
  const replacementOrder =
    detail?.status === 'approved' && detail.replacementOrderId && detail.replacementOrderNumber
      ? { id: detail.replacementOrderId, number: detail.replacementOrderNumber }
      : undefined;
  /** An evidence image whose presign failed arrives with `url: null` (ADO 119316) — unshowable and
   *  unpreviewable, so it is dropped rather than rendered as a broken thumbnail that opens the
   *  lightbox on nothing. Narrowed here so the map below keeps a plain `string`. */
  const images = (detail?.images ?? []).flatMap((image) =>
    image.url ? [{ ...image, url: image.url }] : [],
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={<HeaderTitle status={detail?.status} />}
        hideFooter
      >
        <div className={styles.body}>
          {isPending ? <DrawerSkeleton label="Loading request details" /> : null}
          {isError ? <p className={styles.notice}>{LOAD_ERROR}</p> : null}
          {detail ? (
            <>
              {replacementOrder ? (
                <div className={styles.orderBand}>
                  <span className={styles.orderBandText}>
                    <span className={styles.orderBandCaption}>
                      Your replacement order has been created
                    </span>
                    {/* Verbatim — never reformatted: Figma, the stub and BA disagree three ways on
                        the order-number format, and the payload is the only authority. */}
                    <span className={styles.orderBandNumber}>{replacementOrder.number}</span>
                  </span>
                  <Button
                    variant="neutral"
                    size="small"
                    onClick={() => navigate(routes.order(replacementOrder.id))}
                  >
                    View Order
                  </Button>
                </div>
              ) : null}

              <div className={styles.section}>
                <span className={styles.name}>
                  {unitLabel(detail.itemName, detail.quantity, detail.unitIndex)}
                </span>
                {/* `?? '—'`: a bundle line item carries no `productCategory` (ADO 119316). */}
                <span className={styles.category}>{detail.category ?? '—'}</span>
              </div>

              {images.length ? (
                <ul className={styles.thumbGrid}>
                  {images.map((image, index) => (
                    <li key={image.id}>
                      <button
                        type="button"
                        className={styles.thumbBtn}
                        aria-label={`View image ${index + 1}`}
                        onClick={() => setPreview(image.url)}
                      >
                        <img className={styles.thumbImg} src={image.url} alt="" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className={styles.reasonCard}>
                <h3 className={styles.reasonHeading}>{REPLACEMENT_REASONS[detail.reason]}</h3>
                {detail.description ? (
                  <p className={styles.description}>{detail.description}</p>
                ) : null}
              </div>

              <h3 className={styles.timelineHeading}>Activity Timeline</h3>
              <Timeline
                items={deriveReplacementTimeline(detail).map((event) => ({
                  // Uniform gray dot per Figma — 'pending' maps the dot to --border-default.
                  status: 'pending' as const,
                  title: <Chip intent={timelineChipIntent(event.label)}>{event.label}</Chip>,
                  timestamp: formatTimelineStamp(event.at),
                }))}
              />

              {detail.status === 'rejected' && detail.rejectionReason ? (
                <div className={styles.rejection} role="alert">
                  <span className={styles.rejectionHeading}>
                    <XIcon size={16} aria-hidden />
                    Rejection Reason
                  </span>
                  <p className={styles.rejectionValue}>{detail.rejectionReason}</p>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Drawer>

      {preview ? (
        <ImageLightbox src={preview} label="Item image preview" onClose={() => setPreview(null)} />
      ) : null}
    </>
  );
}
